import { Component, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { PermissionsEnum } from 'src/app/enums/permissions.enum';
import { DatafieldTypes } from 'src/app/models/database/datafield-type.model';
import { Datafield } from 'src/app/models/database/datafield.model';
import { AsyncValidatorsService } from 'src/app/services';
import { PepService } from 'src/app/services/http/pep/pep.service';
import { PermissionService } from 'src/app/services/http/permission/permission.service';
import { SnackbarService } from 'src/app/services/utils/snackbar/snackbar.service';

@Component({
  selector: 'app-add-datafield',
  templateUrl: './add-datafield.component.html',
  styleUrls: ['./add-datafield.component.scss']
})
export class AddDatafieldComponent implements OnInit, OnDestroy {

  verifyUserPermission$: Observable<boolean> = this._permissionService.verify(PermissionsEnum.EDIT_DATASHEET)
  @Input() datasheetSectionId: number

  @ViewChild('addDatafieldModal') addDatafieldModal: TemplateRef<any>
  modalReference: NgbModalRef

  registerDatafieldFormGroup: FormGroup
  formGroupSubmitted: boolean
  registeringDatafield: boolean

  subscriptions: Subscription = new Subscription()

  datafieldTypes$: Observable<DatafieldTypes>


  @ViewChild('datafieldPreviewModal') datafieldPreviewModal: TemplateRef<any>
  @Output() datafieldRegistered: Subject<void> = new Subject<void>()

  constructor(
    private _formBuilder: FormBuilder,
    private _pepService: PepService,
    private _modalService: NgbModal,
    private _permissionService: PermissionService,
    private _snackbarService: SnackbarService,
    private _asyncValidators: AsyncValidatorsService,
  ) { }

  ngOnInit(): void {
    if (!this.datasheetSectionId) {
      throw new Error('Campo datasheetSectionId não informado para o componente AddDatafieldComponent.')
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  openModal(): void {
    this.datafieldTypes$ = this._pepService.getDatafieldTypes()
    this.registerDatafieldFormGroup = this._formBuilder.group({
      description: [undefined, [Validators.required], [this._asyncValidators.datafieldDoesntExistInSection(this.datasheetSectionId)]],
      type: ['1', [Validators.required]],
      order: [0, [Validators.required]],
      minValue: [0, [Validators.required]],
      maxValue: [100, [Validators.required]],
      list: this._formBuilder.array([[undefined]])
    })
    this.formGroupSubmitted = false
    this.registeringDatafield = false
    this.modalReference = this._modalService.open(this.addDatafieldModal, { backdrop: 'static', scrollable: true })
  }

  get formArrayItems(): FormArray {
    return this.registerDatafieldFormGroup.controls['list'] as FormArray
  }

  addItem(): void {
    const newItem = new FormControl('', Validators.required);
    this.formArrayItems.push(newItem)
  }

  removeFormArrayItem(formControlIndex: number): void {
    this.formArrayItems.removeAt(formControlIndex)
  }


  addDatafield(): void {
    this.formGroupSubmitted = true
    if (this.registerDatafieldFormGroup.valid && !this.registeringDatafield) {
      this.registeringDatafield = true
      let datafield: Datafield = {
        datasheetSessionId: this.datasheetSectionId,
        description: this.registerDatafieldFormGroup.controls['description'].value,
        type: this.registerDatafieldFormGroup.controls['type'].value,
        order: this.registerDatafieldFormGroup.controls['order'].value,
      }
      if (this.datafieldNeedsAList) {
        datafield.list = this.registerDatafieldFormGroup.get('list').value.map(item => item.trim())
      }
      if (this.datafieldIsARange) {
        datafield.minValue = this.registerDatafieldFormGroup.controls['minValue'].value
        datafield.maxValue = this.registerDatafieldFormGroup.controls['maxValue'].value
      }
      this._pepService.registerDatafield(datafield)
        .pipe(
          take(1),
          catchError(error => {
            this.registeringDatafield = false
            this._snackbarService.showError('Campo não pôde ser cadastrado, por favor tente novamente mais tarde.')
            return of(null)
          })
        )
        .subscribe((datafieldId: number) => {
          if (datafieldId) {
            this.datafieldRegistered.next()
            this.modalReference.close()
            this._snackbarService.show('Campo da ficha de prontuário cadastrado com sucesso.')
          }
        })
    }
  }

  //getters

  get datafieldDescriptionIsBeingValidated(): boolean {
    return this.registerDatafieldFormGroup.controls['description'].pending
  }

  get datafieldDescriptionIsInvalid(): boolean {
    return this.registerDatafieldFormGroup.controls['description'].invalid
  }

  get datafieldDescriptionWasntInformed(): boolean {
    return this.registerDatafieldFormGroup.controls['description'].errors?.['required']
  }

  get datafieldAlreadyExistsInSection(): boolean {
    return this.registerDatafieldFormGroup.controls['description'].errors?.['datafieldDoesntExistInSection']
  }

  get datafieldTypeIsInvalid(): boolean {
    return this.registerDatafieldFormGroup.controls['type'].invalid
  }

  get datafieldNeedsAList(): boolean {
    return this.registerDatafieldFormGroup.controls['type'].value === '4'
  }

  get datafieldIsARange(): boolean {
    return this.registerDatafieldFormGroup.controls['type'].value === '7'
  }
}